
import { defineComponent, ref, reactive, inject, onMounted, computed, watch, nextTick } from "vue";
import { totalSj, getConstructorsShenghui, bxgsStat, ywyStat, bxgsStatRate, permissionList, onlineSalesStat } from "@/api/shenghuiHttp";
import * as echarts from "echarts";

export default defineComponent({
    name: "Static",
    setup() {
        const setTitle: any = inject("setTitle");
        setTitle(["统计报表"]);

        const headClass = inject("headClass");
        const activeTab = ref("detail");
        const loading = ref(false);
        const roleList = ref(null as any); // null=加载中, {} =已加载
        const allData = ref([] as any[]);
        const leadAllData = ref([] as any[]);
        const leadLoading = ref(false);
        const leadPageSize = ref(10);
        const leadCurrentPage = ref(1);
        const pageSize = ref(10);
        const currentPage = ref(1);
        const summary = reactive({
            totalETime: 0,
            totalGj: 0,
            totalCj: 0,
            cj_rate: 0,
            totalSAmount: 0,
            totalJAmount: 0,
            totalYAmount: 0
        });

        const shortcuts = ref([
            {
                text: "最近一周",
                value: (() => {
                    const end = new Date();
                    const start = new Date();
                    start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
                    return [start, end];
                })(),
            },
            {
                text: "最近一个月",
                value: (() => {
                    const end = new Date();
                    const start = new Date();
                    start.setTime(start.getTime() - 3600 * 1000 * 24 * 30);
                    return [start, end];
                })(),
            },
            {
                text: "最近三个月",
                value: (() => {
                    const end = new Date();
                    const start = new Date();
                    start.setTime(start.getTime() - 3600 * 1000 * 24 * 90);
                    return [start, end];
                })(),
            },
        ] as any);

        const dateRange = ref([] as any);
        const admin_id = ref("");
        const staffList = ref([] as any[]);

        const pagedData = computed(() => {
            const start = (currentPage.value - 1) * pageSize.value;
            return allData.value.slice(start, start + pageSize.value);
        });

        const leadPagedData = computed(() => {
            const start = (leadCurrentPage.value - 1) * leadPageSize.value;
            return leadAllData.value.slice(start, start + leadPageSize.value);
        });

        const formatMoney = (val: any): string => {
            return val ? Number(val).toLocaleString() : '0';
        };

        // 是否有任一 tab 可见
        const hasAnyPermission = computed(() => {
            if (!roleList.value) return false;
            return !!(roleList.value.performance_details || roleList.value.performance_chart || roleList.value.customer_acquisition_data);
        });
        // 权限是否已加载完成
        const permissionLoaded = computed(() => roleList.value !== null);

        const toDateStr = (d: Date): string => `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;

        const getTableData = (): void => {
            loading.value = true;
            // 获取按钮级权限
            let mydata = {
                type: 1,
                role_name: 'new_customer_static',
            };
            permissionList(mydata).then((res: any) => {
                console.log('static.vue permissionList 接口返回数据:', res);
                console.log('static.vue roleList:', res?.content?.list);
                roleList.value = res?.content?.list || 0;
            });
            const data: any = {
                start_time: '',
                end_time: '',
                admin_id: admin_id.value,
            };
            if (dateRange.value && dateRange.value.length === 2 && dateRange.value[0] && dateRange.value[1]) {
                data.start_time = toDateStr(dateRange.value[0]);
                data.end_time = toDateStr(dateRange.value[1]);
            }
            totalSj(data).then((res: any) => {
                const content = res?.content || {};
                allData.value = content.dayList || [];
                currentPage.value = 1;
                // 汇总数据
                summary.totalETime = content.totalETime || 0;
                summary.totalGj = content.totalGj || 0;
                summary.totalCj = content.totalCj || 0;
                summary.cj_rate = content.cj_rate || 0;
                summary.totalSAmount = content.totalSAmount || 0;
                summary.totalJAmount = content.totalJAmount || 0;
                summary.totalYAmount = content.totalYAmount || 0;
                loading.value = false;
            }).catch(() => {
                loading.value = false;
            });
        };

        // 获客数据 tab 专用接口
        const getLeadTableData = (): void => {
            leadLoading.value = true;
            const data: any = {
                start_time: '',
                end_time: '',
                admin_id: admin_id.value,
            };
            if (dateRange.value && dateRange.value.length === 2 && dateRange.value[0] && dateRange.value[1]) {
                data.start_time = toDateStr(dateRange.value[0]);
                data.end_time = toDateStr(dateRange.value[1]);
            }
            onlineSalesStat(data).then((res: any) => {
                leadAllData.value = res?.content || [];
                leadCurrentPage.value = 1;
                leadLoading.value = false;
            }).catch(() => {
                leadLoading.value = false;
            });
        };

        // 图表相关
        const chartMonth = ref(new Date());
        const bxgsChartRef = ref(null as any);
        let bxgsChartInstance: any = null;
        const chartLoading = ref(false);
        const hasChartData = ref(false);
        const chartCache = ref({ names: [] as string[], amounts: [] as number[], rates: [] as number[] });

        const renderChart = () => {
            if (!bxgsChartRef.value) return;
            const { names, amounts, rates } = chartCache.value;
            if (!names.length) return;
            if (!bxgsChartInstance) bxgsChartInstance = echarts.init(bxgsChartRef.value);
            else bxgsChartInstance.resize();
            bxgsChartInstance.setOption({
                tooltip: { trigger: 'axis' },
                legend: { data: ['金额', '占比(%)'] },
                xAxis: { type: 'category', data: names, axisLabel: { rotate: 30 } },
                yAxis: [
                    { type: 'value', name: '金额(元)' },
                    { type: 'value', name: '占比(%)', max: 100 }
                ],
                series: [
                    {
                        name: '金额', type: 'bar', data: amounts, itemStyle: { color: '#1E40AF' },
                        label: { show: true, position: 'top', formatter: '{c}元' }
                    },
                    {
                        name: '占比(%)', type: 'bar', data: rates, yAxisIndex: 1, itemStyle: { color: '#F97316' },
                        label: { show: true, position: 'top', formatter: '{c}%' }
                    }
                ],
                grid: { top: 50, right: 60, bottom: 60, left: 60 },
            }, true);
        };

        const getChartData = () => {
            const data: any = {};
            if (chartMonth.value) {
                const d = new Date(chartMonth.value as any);
                data.month = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
            }
            chartLoading.value = true;
            bxgsStat(data).then((res: any) => {
                chartLoading.value = false;
                const list = (res?.content || []) as any[];
                chartCache.value = {
                    names: list.map((i: any) => i.name),
                    amounts: list.map((i: any) => Number(i.admin_total)),
                    rates: list.map((i: any) => Number(i.rate)),
                };
                if (list.length === 0) {
                    hasChartData.value = false;
                    if (bxgsChartInstance) { bxgsChartInstance.dispose(); bxgsChartInstance = null; }
                    return;
                }
                hasChartData.value = true;
                if (activeTab.value === 'chart') renderChart();
            }).catch(() => {
                chartLoading.value = false;
                chartCache.value = { names: [], amounts: [], rates: [] };
                hasChartData.value = false;
                if (bxgsChartInstance) { bxgsChartInstance.dispose(); bxgsChartInstance = null; }
            });
        };

        // 业务员业绩占比 饼图
        const ywyMonth = ref(new Date());
        const ywyChartRef = ref(null as any);
        const ywyLoading = ref(false);
        const hasYwyData = ref(false);
        const ywyCache = ref({ names: [] as string[], amounts: [] as number[] });
        let ywyChartInstance: any = null;

        const renderYwyChart = () => {
            if (!ywyChartRef.value) return;
            const { names, amounts } = ywyCache.value;
            if (!names.length) return;
            if (!ywyChartInstance) ywyChartInstance = echarts.init(ywyChartRef.value);
            else ywyChartInstance.resize();
            ywyChartInstance.setOption({
                tooltip: { trigger: 'item', formatter: '{b}: ¥{c} ({d}%)' },
                legend: { orient: 'vertical', right: 10, top: 'center', data: names },
                series: [{
                    name: '业绩',
                    type: 'pie',
                    radius: ['40%', '70%'],
                    center: ['40%', '50%'],
                    data: names.map((name: string, i: number) => ({ name, value: amounts[i] })),
                    label: { formatter: '{b}\n{c}元\n{d}%' },
                    itemStyle: {
                        borderRadius: 4,
                        borderColor: '#fff',
                        borderWidth: 2,
                    },
                }],
            }, true);
        };

        const getYwyData = () => {
            const data: any = {};
            if (ywyMonth.value) {
                const d = new Date(ywyMonth.value as any);
                data.month = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
            }
            ywyLoading.value = true;
            ywyStat(data).then((res: any) => {
                ywyLoading.value = false;
                const list = (res?.content || []) as any[];
                ywyCache.value = {
                    names: list.map((i: any) => i.name),
                    amounts: list.map((i: any) => Number(i.admin_total)),
                };
                if (list.length === 0) {
                    hasYwyData.value = false;
                    if (ywyChartInstance) { ywyChartInstance.dispose(); ywyChartInstance = null; }
                    return;
                }
                hasYwyData.value = true;
                if (activeTab.value === 'chart') renderYwyChart();
            }).catch(() => {
                ywyLoading.value = false;
                ywyCache.value = { names: [], amounts: [] };
                hasYwyData.value = false;
                if (ywyChartInstance) { ywyChartInstance.dispose(); ywyChartInstance = null; }
            });
        };

        // 达成率 折线图
        const rateMonth = ref(new Date());
        const rateChartRef = ref(null as any);
        const rateLoading = ref(false);
        const hasRateData = ref(false);
        const rateCache = ref({ names: [] as string[], totals: [] as number[], rates: [] as number[] });
        let rateChartInstance: any = null;

        const renderRateChart = () => {
            if (!rateChartRef.value) return;
            const { names, totals, rates } = rateCache.value;
            if (!names.length) return;
            if (!rateChartInstance) rateChartInstance = echarts.init(rateChartRef.value);
            else rateChartInstance.resize();
            rateChartInstance.setOption({
                tooltip: { trigger: 'axis' },
                legend: { data: ['实际金额', '达成率(%)'] },
                xAxis: { type: 'category', data: names, axisLabel: { rotate: 30 } },
                yAxis: [
                    { type: 'value', name: '金额(元)' },
                    { type: 'value', name: '达成率(%)' }
                ],
                series: [
                    {
                        name: '实际金额', type: 'bar', data: totals, itemStyle: { color: '#1E40AF' },
                        label: { show: true, position: 'top', formatter: '{c}元' }
                    },
                    {
                        name: '达成率(%)', type: 'line', data: rates, yAxisIndex: 1,
                        itemStyle: { color: '#F97316' },
                        label: { show: true, formatter: (params: any) => params.value === 0 ? '' : params.value + '%' }
                    },
                ],
                grid: { top: 50, right: 60, bottom: 60, left: 60 },
            }, true);
        };

        const getRateData = () => {
            const data: any = {};
            if (rateMonth.value) {
                const d = new Date(rateMonth.value as any);
                data.month = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
            }
            rateLoading.value = true;
            bxgsStatRate(data).then((res: any) => {
                rateLoading.value = false;
                const list = (res?.content || []) as any[];
                rateCache.value = {
                    names: list.map((i: any) => i.name),
                    totals: list.map((i: any) => Number(i.total)),
                    rates: list.map((i: any) => Number(i.rate)),
                };
                if (list.length === 0) {
                    hasRateData.value = false;
                    if (rateChartInstance) { rateChartInstance.dispose(); rateChartInstance = null; }
                    return;
                }
                hasRateData.value = true;
                if (activeTab.value === 'chart') renderRateChart();
            }).catch(() => {
                rateLoading.value = false;
                rateCache.value = { names: [], totals: [], rates: [] };
                hasRateData.value = false;
                if (rateChartInstance) { rateChartInstance.dispose(); rateChartInstance = null; }
            });
        };

        const handleSizeChange = (size: number) => { pageSize.value = size; currentPage.value = 1; };
        const handleCurrentChange = (page: number) => { currentPage.value = page; };
        const handleLeadSizeChange = (size: number) => { leadPageSize.value = size; leadCurrentPage.value = 1; };
        const handleLeadCurrentChange = (page: number) => { leadCurrentPage.value = page; };

        // 权限加载后，若当前 tab 无权限则自动切换到第一个可用 tab
        watch(roleList, (val) => {
            const tabs = [
                { name: 'detail', perm: val.performance_details },
                { name: 'chart', perm: val.performance_chart },
                { name: 'lead', perm: val.customer_acquisition_data },
            ];
            const firstAvailable = tabs.find(t => t.perm);
            const currentAvailable = tabs.find(t => t.name === activeTab.value && t.perm);
            if (!currentAvailable && firstAvailable) {
                activeTab.value = firstAvailable.name;
            }
        });

        // 首次加载图表数据
        getChartData();
        getYwyData();
        getRateData();
        // 切到获客数据 tab 时拉取数据
        watch(activeTab, (v) => {
            if (v === 'lead' && leadAllData.value.length === 0) {
                getLeadTableData();
            }
        });
        // 切到图表 tab 时重新渲染
        watch(activeTab, (v) => {
            if (v === 'chart') {
                nextTick(() => {
                    if (bxgsChartInstance) bxgsChartInstance.resize(); else renderChart();
                    if (ywyChartInstance) ywyChartInstance.resize(); else renderYwyChart();
                    if (rateChartInstance) rateChartInstance.resize(); else renderRateChart();
                });
            }
        });

        onMounted(() => {
            getConstructorsShenghui({}).then((res: any) => {
                staffList.value = res?.content?.staff || [];
            });
            getTableData();
        });

        return {
            activeTab,
            headClass,
            loading,
            roleList,
            hasAnyPermission,
            permissionLoaded,
            allData,
            pagedData,
            pageSize,
            currentPage,
            dateRange,
            admin_id,
            staffList,
            summary,
            formatMoney,
            getTableData,
            handleSizeChange,
            handleCurrentChange,
            handleLeadSizeChange,
            handleLeadCurrentChange,
            leadAllData,
            leadLoading,
            leadPagedData,
            leadPageSize,
            leadCurrentPage,
            getLeadTableData,
            shortcuts,
            chartMonth,
            bxgsChartRef,
            chartLoading,
            hasChartData,
            getChartData,
            ywyMonth,
            ywyChartRef,
            ywyLoading,
            hasYwyData,
            getYwyData,
            rateMonth,
            rateChartRef,
            rateLoading,
            hasRateData,
            getRateData
        };
    },
});
